import React from 'react';
import { Icon } from '@iconify/react';
import FormInput from '../../../../../components/child/FormInput';
import FormSelect from '../../../../../components/child/FormSelect';
import FormButton from '../../../../../components/child/FormButton';

const OpeningBalanceModal = ({
    show,
    handleClose,
    handleSubmit,
    formData,
    setFormData,
    editId,
    wingsDropdown,
    flatsDropdown,
    selectedFlatOption,
    invoiceTypesData,
    loading,
    isReadOnly
}) => {
    // The shared flatsDropdown slice is written by both the list filter and this
    // form, so it may not contain the flat this record actually points at (and it
    // only ever carries ACTIVE flats). Merging the row's own flat in guarantees
    // the saved selection renders instead of falling back to the placeholder.
    const flatOptions = React.useMemo(() => {
        const list = flatsDropdown || [];
        if (!selectedFlatOption?.flat_id) return list;
        return list.some(f => String(f.flat_id) === String(selectedFlatOption.flat_id))
            ? list
            : [selectedFlatOption, ...list];
    }, [flatsDropdown, selectedFlatOption]);

    if (!show) return null;

    const totalAmount = (
        (parseFloat(formData.principal_amount) || 0) +
        (parseFloat(formData.interest_amount) || 0) +
        (parseFloat(formData.tax_amount) || 0) -
        (parseFloat(formData.advance_amount) || 0)
    ).toFixed(2);

    const statusOptions = [
        { value: 'DRAFT', label: 'Draft' },
        { value: 'POSTED', label: 'Posted' }
    ];

    const financialYearOptions = [
        { value: '2023-24', label: '2023-24' },
        { value: '2024-25', label: '2024-25' },
        { value: '2025-26', label: '2025-26' },
        { value: '2026-27', label: '2026-27' }
    ];

    return (
        <div className="modal-backdrop-custom" onClick={handleClose}>
            <div className="modal-content-custom card radius-16 border-0 shadow-lg" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '640px' }}>
                <div className="card-header py-10 px-16 border-bottom d-flex align-items-center justify-content-between">
                    <h6 className="fw-semibold mb-0">
                        {isReadOnly ? 'View' : editId ? 'Edit' : 'Add New'} Opening Balance
                    </h6>
                    <button
                        onClick={handleClose}
                        className="btn btn-link p-0 text-secondary-light"
                    >
                        <Icon icon="mdi:close" className="text-xl" />
                    </button>
                </div>
                <form onSubmit={handleSubmit}>
                    <div className="card-body p-24" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                        <div className="row gy-3">
                            {/* Wing & Flat Selection */}
                            <div className="col-6">
                                <FormSelect
                                    label="Wing"
                                    name="wing_id"
                                    value={formData.wing_id}
                                    onChange={(e) => setFormData({ ...formData, wing_id: e.target.value, flat_id: '' })}
                                    options={wingsDropdown || []}
                                    valueKey="wing_id"
                                    labelKey="title"
                                    placeholder="-- Select Wing --"
                                    required={true}
                                    disabled={isReadOnly || !!editId}
                                    className=""
                                />
                            </div>
                            <div className="col-6">
                                <FormSelect
                                    label="Flat"
                                    name="flat_id"
                                    value={formData.flat_id}
                                    onChange={(e) => setFormData({ ...formData, flat_id: e.target.value })}
                                    options={flatOptions}
                                    valueKey="flat_id"
                                    labelKey="flat_number"
                                    placeholder="-- Select Flat --"
                                    required={true}
                                    disabled={isReadOnly || !formData.wing_id || !!editId}
                                    className=""
                                />
                            </div>

                            {/* Invoice Type */}
                            <div className="col-12">
                                <FormSelect
                                    label="Invoice Type"
                                    name="invoice_type_id"
                                    value={formData.invoice_type_id}
                                    onChange={(e) => setFormData({ ...formData, invoice_type_id: e.target.value })}
                                    options={invoiceTypesData || []}
                                    valueKey="invoice_type_id"
                                    labelKey="invoice_title"
                                    placeholder="-- Select Invoice Type (Optional) --"
                                    disabled={isReadOnly}
                                    className=""
                                />
                            </div>

                            {/* Balance Date & Financial Year */}
                            <div className="col-6">
                                <FormInput
                                    label="Balance Date"
                                    type="date"
                                    name="balance_date"
                                    value={formData.balance_date}
                                    onChange={(e) => setFormData({ ...formData, balance_date: e.target.value })}
                                    required={true}
                                    disabled={isReadOnly}
                                    className=""
                                />
                            </div>
                            <div className="col-6">
                                <FormSelect
                                    label="Financial Year"
                                    name="financial_year"
                                    value={formData.financial_year}
                                    onChange={(e) => setFormData({ ...formData, financial_year: e.target.value })}
                                    options={financialYearOptions}
                                    valueKey="value"
                                    labelKey="label"
                                    placeholder="-- Select FY --"
                                    required={true}
                                    disabled={isReadOnly}
                                    className=""
                                />
                            </div>

                            {/* Amount Fields */}
                            <div className="col-12">
                                <label className="form-label text-secondary-light text-sm fw-semibold mb-2">
                                    Amount Details
                                </label>
                                <div className="p-16 bg-neutral-50 radius-8 border">
                                    <div className="row gy-3">
                                        <div className="col-6">
                                            <FormInput
                                                label="Principal Amount"
                                                type="number"
                                                name="principal_amount"
                                                value={formData.principal_amount}
                                                onChange={(e) => setFormData({ ...formData, principal_amount: e.target.value })}
                                                placeholder="0.00"
                                                disabled={isReadOnly}
                                                className=""
                                            />
                                        </div>
                                        <div className="col-6">
                                            <FormInput
                                                label="Interest Amount"
                                                type="number"
                                                name="interest_amount"
                                                value={formData.interest_amount}
                                                onChange={(e) => setFormData({ ...formData, interest_amount: e.target.value })}
                                                placeholder="0.00"
                                                disabled={isReadOnly}
                                                className=""
                                            />
                                        </div>
                                        <div className="col-6">
                                            <FormInput
                                                label="Tax Amount"
                                                type="number"
                                                name="tax_amount"
                                                value={formData.tax_amount}
                                                onChange={(e) => setFormData({ ...formData, tax_amount: e.target.value })}
                                                placeholder="0.00"
                                                disabled={isReadOnly}
                                                className=""
                                            />
                                        </div>
                                        <div className="col-6">
                                            <FormInput
                                                label="Advance Amount"
                                                type="number"
                                                name="advance_amount"
                                                value={formData.advance_amount}
                                                onChange={(e) => setFormData({ ...formData, advance_amount: e.target.value })}
                                                placeholder="0.00"
                                                disabled={isReadOnly}
                                                className=""
                                            />
                                        </div>
                                    </div>

                                    {/* Total Amount Display */}
                                    <div className="mt-12 pt-12 border-top d-flex align-items-center justify-content-between">
                                        <span className="fw-semibold text-secondary-light">Total Amount</span>
                                        <span className={`fw-bold text-lg ${parseFloat(totalAmount) >= 0 ? 'text-danger-600' : 'text-success-600'}`}>
                                            {parseFloat(totalAmount) >= 0 ? '' : '-'} ₹{Math.abs(parseFloat(totalAmount)).toFixed(2)}
                                        </span>
                                    </div>
                                    <div className="mt-4">
                                        <small className="text-secondary-light">
                                            {parseFloat(totalAmount) >= 0
                                                ? 'Member owes society'
                                                : 'Advance / Credit balance'}
                                        </small>
                                    </div>
                                </div>
                            </div>

                            {/* Narration */}
                            <div className="col-12">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Narration
                                </label>
                                <textarea
                                    className="form-control"
                                    name="narration"
                                    value={formData.narration}
                                    onChange={(e) => setFormData({ ...formData, narration: e.target.value })}
                                    placeholder="e.g., Opening balance from previous system"
                                    rows={2}
                                    maxLength={500}
                                    disabled={isReadOnly}
                                />
                            </div>

                            {/* Reference Number & Status */}
                            <div className="col-6">
                                <FormInput
                                    label="Reference Number"
                                    name="reference_number"
                                    value={formData.reference_number}
                                    onChange={(e) => setFormData({ ...formData, reference_number: e.target.value })}
                                    placeholder="e.g., OB-001"
                                    maxLength={50}
                                    disabled={isReadOnly}
                                    className=""
                                />
                            </div>
                            <div className="col-6">
                                <FormSelect
                                    label="Status"
                                    name="status"
                                    value={formData.status}
                                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                                    options={statusOptions}
                                    valueKey="value"
                                    labelKey="label"
                                    disabled={isReadOnly}
                                    className=""
                                />
                            </div>

                            {/* Remarks */}
                            <div className="col-12">
                                <label className="form-label text-secondary-light text-sm fw-medium mb-1">
                                    Remarks
                                </label>
                                <textarea
                                    className="form-control"
                                    name="remarks"
                                    value={formData.remarks}
                                    onChange={(e) => setFormData({ ...formData, remarks: e.target.value })}
                                    placeholder="Additional remarks (optional)"
                                    rows={2}
                                    disabled={isReadOnly}
                                />
                            </div>
                        </div>
                    </div>
                    {!isReadOnly && (
                        <div className="card-footer py-10 px-16 border-top d-flex justify-content-end gap-2">
                            <FormButton
                                onClick={handleClose}
                                variant="outline-secondary"
                                className="btn-outline-secondary"
                            >
                                Cancel
                            </FormButton>
                            <FormButton
                                type="submit"
                                variant="primary"
                                icon={editId ? "mdi:check" : "mdi:plus"}
                                isLoading={loading}
                            >
                                {editId ? 'Update' : 'Add'} Opening Balance
                            </FormButton>
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
};

export default OpeningBalanceModal;
